import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Calendar, MapPin, ArrowRight, Mic2, Building2, CalendarDays, Megaphone, Tag, Sparkles, Trophy, FerrisWheel, Sailboat, Store } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { EventCard, VenueCard, ArtistCard, AttractionCard, ActivityCard, fmtDate, fmtDateTime, cardGrid } from "../components/Cards";
import { SpotlightCard, SpotlightEmbed, LiveBadge } from "./BarDad";
import { STATES } from "../lib/constants";
import { WishlistHeart } from "../components/WishlistHeart";
import { useSuggestSearch, SuggestDropdown, useOutsideClose } from "../components/SearchSuggestions";

const HERO = "https://images.pexels.com/photos/3122799/pexels-photo-3122799.jpeg";
const COAST = "https://images.unsplash.com/photo-1769652944068-1600f06f30d6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const ENTITY_PATH = { artist: "artists", venue: "venues", event: "events", attraction: "attractions", activity: "activities" };

function RewardsRow() {
  const [programs, setPrograms] = useState([]);
  useEffect(() => { api.get("/loyalty/featured").then((r) => setPrograms(r.data)).catch(() => {}); }, []);
  if (!programs.length) return null;
  return (
    <section className="px-6 md:px-12 lg:px-20 py-14 border-y border-white/5 bg-gradient-to-b from-amber-500/[0.04] to-transparent" data-testid="home-rewards-row">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="overline text-amber-400">Gulf Coast VIP Rewards</p>
          <h2 className="font-display font-bold text-2xl md:text-3xl">Earn rewards across the coast</h2>
          <p className="text-zinc-400 mt-1 text-sm">Join fan clubs and loyalty programs — earn points on purchases, tickets and visits.</p>
        </div>
        <Link to="/my-rewards" className="hidden sm:flex items-center gap-1.5 text-sm text-amber-400 hover:text-amber-300 font-medium shrink-0">My rewards <ArrowRight size={15} /></Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {programs.map((p) => {
          const href = p.related?.entity_slug ? `/${ENTITY_PATH[p.related.entity_type] || "artists"}/${p.related.entity_slug}` : "/my-rewards";
          return (
            <Link key={p.id} to={href} className="group bg-[#111] border border-white/10 rounded-2xl p-5 hover:border-amber-500/50 transition-colors" data-testid={`home-reward-${p.slug}`}>
              <div className="flex items-center gap-3">
                {p.image_url ? <img src={fileUrl(p.image_url)} alt="" className="w-12 h-12 rounded-xl object-cover" /> :
                  <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center"><Sparkles className="text-amber-400" size={20} /></div>}
                <div className="min-w-0">
                  <p className="font-display font-bold leading-snug truncate">{p.name}</p>
                  <p className="text-xs text-zinc-500 truncate">{p.related?.entity_name || "Gulf Coast VIP"} · {p.member_count} member{p.member_count === 1 ? "" : "s"}</p>
                </div>
              </div>
              <p className="text-sm text-zinc-400 mt-3 line-clamp-2">{p.description || `Earn ${p.points_name || "points"} on purchases, tickets and visits — redeem for exclusive rewards.`}</p>
              {p.welcome_bonus > 0 && <p className="text-xs text-amber-300 font-bold mt-2">★ {p.welcome_bonus} {p.points_name || "points"} welcome bonus</p>}
              <span className="inline-block mt-3 text-xs font-bold text-amber-400 group-hover:translate-x-1 transition-transform">Join rewards →</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}


function Section({ title, kicker, link, children }) {
  return (
    <section className="px-6 md:px-12 lg:px-20 py-14">
      <div className="flex items-end justify-between mb-8">
        <div>
          {kicker && <p className="overline mb-2">{kicker}</p>}
          <h2 className="font-display font-extrabold text-3xl md:text-4xl">{title}</h2>
        </div>
        {link && <Link to={link} className="text-sm text-zinc-400 hover:text-white flex items-center gap-1 shrink-0">View all <ArrowRight size={15} /></Link>}
      </div>
      {children}
    </section>
  );
}

function FeaturedWeek({ data }) {
  const { gigs = [], deals = [], sponsors = [] } = data || {};
  if (!gigs.length && !deals.length && !sponsors.length) return null;
  return (
    <section className="px-6 md:px-12 lg:px-20 py-14 border-y border-white/5 bg-gradient-to-b from-rose-500/[0.05] to-transparent" data-testid="featured-week">
      <div className="flex items-center gap-3 mb-9">
        <Sparkles className="text-rose-400 shrink-0" size={22} />
        <div>
          <p className="overline">Hot right now</p>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl">Featured This Week</h2>
        </div>
      </div>

      {gigs.length > 0 && (
        <div className="mb-12">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><Megaphone size={18} className="text-rose-400" /> Open & awarded gigs</h3>
            <Link to="/gigs" className="text-sm text-rose-400">Browse gigs →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {gigs.slice(0, 6).map((g) => (
              <Link key={g.id} to={`/gigs/${g.slug}`} className="block bg-[#111] border border-white/10 rounded-2xl p-5 hover:border-rose-500/40 transition-colors" data-testid={`fw-gig-${g.id}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-xs px-2.5 py-1 rounded-full bg-rose-500/15 text-rose-300 capitalize">{g.status}</span>
                  {g.event_date && <span className="text-xs text-zinc-500">{fmtDate(g.event_date)}</span>}
                </div>
                <p className="font-display font-bold text-lg leading-tight">{g.title}</p>
                <p className="text-sm text-zinc-400 mt-1 flex items-center gap-1"><MapPin size={13} /> {g.city}{g.state ? `, ${g.state}` : ""}</p>
                {(g.budget_min || g.budget_max) && <p className="text-sm text-zinc-300 mt-2">Budget: ${g.budget_min || "?"}–${g.budget_max || "?"}</p>}
              </Link>
            ))}
          </div>
        </div>
      )}

      {deals.length > 0 && (
        <div className="mb-12">
          <h3 className="font-display font-bold text-lg flex items-center gap-2 mb-5"><Tag size={18} className="text-orange-400" /> Hot deals</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {deals.slice(0, 6).map((d) => (
              <Link key={d.id} to={d.venue_slug ? `/venues/${d.venue_slug}` : "#"} className="block bg-[#111] border border-white/10 rounded-2xl p-5 hover:border-orange-500/40 transition-colors" data-testid={`fw-deal-${d.id}`}>
                <p className="font-display font-bold text-lg leading-tight">{d.title}</p>
                <p className="text-sm text-zinc-400 mt-1">{d.venue_name}</p>
                <div className="flex items-center gap-2 mt-3">
                  {d.discounted_price && <span className="font-display font-black text-xl text-orange-300">${d.discounted_price}</span>}
                  {d.original_price && <span className="text-sm text-zinc-500 line-through">${d.original_price}</span>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {sponsors.length > 0 && (
        <div>
          <p className="overline mb-4">Proudly supported by</p>
          <div className="flex flex-wrap items-center gap-3">
            {sponsors.map((s) => (
              <div key={s.id} className="flex items-center gap-2 bg-[#111] border border-white/10 rounded-full px-4 py-2" data-testid={`fw-sponsor-${s.id}`}>
                {s.logo ? <img src={fileUrl(s.logo)} alt={s.name} className="w-7 h-7 rounded-full object-cover" /> : <span className="w-7 h-7 rounded-full bg-rose-500/20 text-rose-300 flex items-center justify-center text-xs font-bold">{s.name?.[0]}</span>}
                <span className="text-sm font-medium">{s.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function AwardsHome({ count }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/awards/home-summary?limit=${count || 6}`).then((r) => setData(r.data)).catch(() => {});
  }, [count]);
  if (!data?.season || !data.categories?.length) return null;
  const s = data.season;
  const statusLabel = s.status === "voting_open" ? "Voting is open" : s.status === "nominations_open" ? "Nominations are open" : "Winners announced";
  return (
    <Section title={s.name || "VIP Spotlight Awards"} kicker={statusLabel} link={`/awards/${s.slug}`}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="home-awards">
        {data.categories.map((c) => (
          <Link key={c.slug} to={`/awards/${s.slug}/${c.slug}`} className="group bg-[#111] border border-white/10 hover:border-rose-500/50 rounded-2xl p-5 transition-colors" data-testid={`home-award-${c.slug}`}>
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-display font-bold text-lg leading-snug">{c.name}</h3>
              <Trophy size={18} className="text-amber-400 shrink-0 mt-0.5" />
            </div>
            <p className="text-xs text-zinc-500 mt-1 capitalize">{c.entity_type} · {c.nominee_count} nominee{c.nominee_count === 1 ? "" : "s"}</p>
            {c.nominees?.length > 0 && (
              <p className="text-sm text-zinc-400 mt-3 line-clamp-2">{c.nominees.join(" · ")}{c.nominee_count > c.nominees.length ? " …" : ""}</p>
            )}
            <span className="inline-flex items-center gap-1 text-sm text-rose-400 mt-4 group-hover:gap-2 transition-all">
              {s.status === "voting_open" ? "Vote now" : s.status === "nominations_open" ? "Nominate" : "See results"} <ArrowRight size={14} />
            </span>
          </Link>
        ))}
      </div>
    </Section>
  );
}

function SpotlightHero({ sp, type }) {
  const e = sp.entity;
  const img = e.hero_image || e.profile_image || e.event_image;
  const link = `/${type === "artist" ? "artists" : "venues"}/${e.slug}`;
  return (
    <Link to={link} className="relative block rounded-3xl overflow-hidden mb-8 group" data-testid={`spotlight-${type}`}>
      <div className="aspect-[21/8] min-h-[220px]">
        {img && <img src={fileUrl(img)} alt={e.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-transparent" />
        <div className="absolute inset-0 p-8 md:p-12 flex flex-col justify-end">
          <span className="self-start bg-rose-500 text-white text-xs font-extrabold px-3 py-1 rounded-full mb-3">★ {sp.spotlight.badge_text}</span>
          <h3 className="font-display font-black text-3xl md:text-5xl">{e.name}</h3>
          <p className="text-zinc-300 mt-2 max-w-lg line-clamp-2">{e.tagline || e.description || e.bio}</p>
        </div>
      </div>
    </Link>
  );
}

function MarketplaceShelf() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/marketplace/featured?limit=8").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);
  if (!items || items.length === 0) return null;
  const money = (v) => `$${Number(v || 0).toFixed(2)}`;
  return (
    <Section title="From the Marketplace" kicker="Merch · Tickets · Memberships" link="/marketplace">
      <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4" data-testid="home-marketplace-shelf">
        {items.map((p) => {
          const img = (p.images && p.images[0]) || p.image_url;
          const hasSale = p.sale_price > 0 && p.sale_price < p.price;
          return (
            <Link key={p.id} to={`/marketplace/${p.slug}`} className="group bg-[#111] border border-white/10 hover:border-rose-500/50 rounded-2xl overflow-hidden transition-colors" data-testid={`shelf-product-${p.slug}`}>
              <div className="aspect-square bg-[#0a0a0a] relative overflow-hidden">
                {img ? <img src={fileUrl(img)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> :
                  <div className="w-full h-full flex items-center justify-center text-zinc-700"><Tag size={40} /></div>}
                {p.listing && (
                  <span className="absolute top-2 left-2 bg-black/70 backdrop-blur text-white text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded" data-testid="shelf-listing-badge">
                    {p.listing.name}
                  </span>
                )}
                <WishlistHeart productId={p.id} overlay />
              </div>
              <div className="p-4">
                <p className="text-[10px] uppercase tracking-wide text-rose-400 font-bold mb-1">{p.seller?.display_name || "Gulf Coast VIP"}</p>
                <h3 className="font-semibold text-sm leading-tight line-clamp-2 min-h-[2.5rem]">{p.name}</h3>
                <div className="flex items-center gap-2 mt-2">
                  <span className="font-display font-bold text-lg text-rose-400">{money(hasSale ? p.sale_price : p.price)}</span>
                  {hasSale && <span className="text-xs text-zinc-500 line-through">{money(p.price)}</span>}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </Section>
  );
}

// Hero search box with the shared live-suggestions dropdown. Kept separate
// so the useSuggestSearch hook only fires when this section renders.
function HeroSearch({ q, setQ, loc, setLoc, doSearch }) {
  const [focused, setFocused] = useState(false);
  const nav = useNavigate();
  const wrapRef = useRef(null);
  const { loading, items } = useSuggestSearch(q);
  useOutsideClose(wrapRef, () => setFocused(false));
  const goSearch = () => { const t = q.trim(); if (!t) return; setFocused(false); nav(`/search?q=${encodeURIComponent(t)}${loc ? `&city=${encodeURIComponent(loc)}` : ""}`); };
  const goPath = (p) => { setFocused(false); setQ(""); nav(p); };
  return (
    <div ref={wrapRef} className="relative mt-8" data-testid="hero-search-wrap">
      <form onSubmit={doSearch} className="glass rounded-2xl p-3 flex flex-col sm:flex-row gap-3" data-testid="hero-search">
        <div className="flex items-center gap-2 flex-1 px-3">
          <Search size={18} className="text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => setFocused(true)} autoComplete="off" placeholder="Search everything — events, venues, artists, deals, jobs, gift cards…" className="bg-transparent outline-none w-full py-2 placeholder:text-zinc-500" data-testid="hero-search-keyword" />
        </div>
        <div className="flex items-center gap-2 flex-1 px-3 border-t sm:border-t-0 sm:border-l border-white/10">
          <MapPin size={18} className="text-zinc-400" />
          <input value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="City" className="bg-transparent outline-none w-full py-2 placeholder:text-zinc-500" data-testid="hero-search-location" />
        </div>
        <button className="bg-rose-500 hover:bg-rose-600 font-bold px-7 py-3 rounded-xl transition-colors" data-testid="hero-search-btn">Search</button>
      </form>
      {focused && (
        <SuggestDropdown
          query={q}
          items={items}
          loading={loading}
          onPick={goPath}
          onSeeAll={goSearch}
          className="absolute top-full left-0 right-0 sm:right-auto sm:w-[520px] mt-2"
          testidPrefix="hero-search"
        />
      )}
    </div>
  );
}


export default function Home() {
  const { settings } = useSettings();
  const sec = settings.sections || {};
  const cstyle = settings.card_styles || {};
  const [data, setData] = useState({ events: [], venues: [], artists: [], states: {}, upcoming: [], featured: { gigs: [], deals: [], sponsors: [] } });
  const [spot, setSpot] = useState({});
  const [barDad, setBarDad] = useState(null);
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState("");
  const [email, setEmail] = useState("");
  const nav = useNavigate();

  const headlineWords = (settings.hero_headline || "").split(" ");
  const headLast = headlineWords.slice(-2).join(" ");
  const headFirst = headlineWords.slice(0, -2).join(" ");

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/home-data");
      setData({ events: data.events, venues: data.venues, artists: data.artists,
                states: data.states, upcoming: data.upcoming, featured: data.featured,
                attractions: data.attractions || [], activities: data.activities || [] });
      api.get("/spotlights/current").then((r) => setSpot(r.data)).catch(() => {});
      api.get("/bar-dad/live-status").then((r) => setBarDad(r.data)).catch(() => {});
    })();
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (loc) p.set("city", loc);
    // Universal search — covers events, venues, artists, attractions, activities, businesses, deals, gigs, hiring, spotlights, kb, products.
    nav(`/search?${p.toString()}`);
  };

  const subscribe = async (e) => {
    e.preventDefault();
    await api.post("/newsletter", { email });
    toast.success("You're on the list! 🌴");
    setEmail("");
  };

  const cities = [
    { city: "Destin", img: COAST }, { city: "Pensacola", img: HERO },
    { city: "Tampa", img: "https://images.unsplash.com/photo-1618853802485-be85de3d160f?w=600&q=80" },
    { city: "Mobile", img: "https://images.unsplash.com/photo-1780680546635-eeda688fa493?w=600&q=80" },
    { city: "Gulf Shores", img: "https://images.pexels.com/photos/27795342/pexels-photo-27795342.jpeg?w=600" },
  ];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center px-6 md:px-12 lg:px-20">
        <img src={settings.hero_image || HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/40" />
        <div className="relative max-w-3xl fade-up">
          <p className="overline mb-4">Florida · Alabama · Mississippi · Louisiana · Texas</p>
          <h1 className="font-display font-black text-5xl md:text-7xl leading-[0.95]">
            {headFirst && <>{headFirst}<br /></>}<span className="gradient-text">{headLast}</span>
          </h1>
          <p className="text-lg text-zinc-300 mt-6 max-w-xl">{settings.hero_tagline}</p>
          <HeroSearch q={q} setQ={setQ} loc={loc} setLoc={setLoc} doSearch={doSearch} />
          <div className="flex flex-wrap items-center gap-2 mt-6 text-sm" data-testid="hero-explore-row">
            <span className="text-zinc-400 uppercase tracking-widest text-[11px] font-bold mr-1">Explore</span>
            {[
              { to: "/events", Icon: CalendarDays, label: "Events" },
              { to: "/venues", Icon: Building2, label: "Venues" },
              { to: "/artists", Icon: Mic2, label: "Artists" },
              { to: "/attractions", Icon: FerrisWheel, label: "Attractions" },
              { to: "/activities", Icon: Sailboat, label: "Activities" },
              { to: "/businesses", Icon: Store, label: "Businesses" },
              { to: "/deals", Icon: Tag, label: "Deals" },
            ].map(({ to, Icon, label }) => (
              <Link key={to} to={to} className="flex items-center gap-1.5 glass px-3.5 py-1.5 rounded-full hover:border-rose-500/60 hover:text-rose-300 transition-colors" data-testid={`hero-explore-${label.toLowerCase()}`}>
                <Icon size={14} /> {label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {sec.featured_events && (
      <Section title="Featured Events" kicker="Don't miss out" link="/events">
        <div className={`grid gap-5 ${cardGrid(cstyle.events, "sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4")}`}>
          {data.events.slice(0, 4).map((e) => <EventCard key={e.id} e={e} style={cstyle.events} />)}
        </div>
      </Section>
      )}

      {sec.featured_week !== false && <FeaturedWeek data={data.featured} />}

      {/* Popular cities */}
      {sec.popular_cities && (
      <Section title="Popular Cities" kicker="Where to go">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {cities.map((c) => (
            <Link key={c.city} to={`/events?city=${c.city}`} className="relative aspect-square rounded-xl overflow-hidden group" data-testid={`city-${c.city}`}>
              <img src={c.img} alt={c.city} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-end p-3">
                <span className="font-display font-bold">{c.city}</span>
              </div>
            </Link>
          ))}
        </div>
      </Section>
      )}

      {sec.featured_venues && (
      <Section title={spot.venue ? "Venue Spotlight" : "Featured Venues"} kicker="The hottest spots" link="/venues">
        {spot.venue && <SpotlightHero sp={spot.venue} type="venue" />}
        <div className={`grid gap-5 ${cardGrid(cstyle.venues, "md:grid-cols-2 lg:grid-cols-3")}`}>
          {data.venues.slice(0, 3).map((v) => <VenueCard key={v.id} v={v} style={cstyle.venues} />)}
        </div>
      </Section>
      )}

      {sec.featured_artists && (
      <Section title={spot.artist ? "Artist Spotlight" : "Featured Artists"} kicker="On the rise" link="/artists">
        {spot.artist && <SpotlightHero sp={spot.artist} type="artist" />}
        <div className={`grid gap-5 ${cardGrid(cstyle.artists, "sm:grid-cols-2 md:grid-cols-4")}`}>
          {data.artists.slice(0, 4).map((a) => <ArtistCard key={a.id} a={a} style={cstyle.artists} />)}
        </div>
      </Section>
      )}

      {sec.featured_attractions !== false && (data.attractions || []).length > 0 && (
      <Section title="Featured Attractions" kicker="Places to visit" link="/attractions">
        <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-4">
          {data.attractions.slice(0, 4).map((a) => <AttractionCard key={a.id} a={a} />)}
        </div>
      </Section>
      )}

      {sec.featured_activities !== false && (data.activities || []).length > 0 && (
      <Section title="Featured Activities" kicker="Experiences & tours" link="/activities">
        <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-4">
          {data.activities.slice(0, 4).map((a) => <ActivityCard key={a.id} a={a} />)}
        </div>
      </Section>
      )}

      {sec.marketplace_shelf !== false && <MarketplaceShelf />}

      {sec.bar_dad !== false && barDad && (barDad.live || barDad.latest || barDad.featured) && (
      <Section title="Bar Dad Spotlight" kicker="Gulf Coast VIP Original" link="/bar-dad">
        {barDad.live && barDad.banner_enabled ? (
          <div data-testid="home-bardad-live">
            <div className="flex flex-wrap items-center gap-3 mb-4"><LiveBadge /><h3 className="font-display font-bold text-xl">{barDad.live.title}</h3></div>
            <SpotlightEmbed s={barDad.live} />
            <div className="mt-4 flex gap-3">
              <Link to={`/bar-dad/${barDad.live.slug}`} className="bg-red-600 hover:bg-red-700 font-bold text-sm px-5 py-2.5 rounded-full">Watch Live</Link>
              <Link to="/bar-dad" className="bg-white/10 hover:bg-white/20 font-bold text-sm px-5 py-2.5 rounded-full">View All Spotlights</Link>
            </div>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-bardad">
            {[barDad.featured, barDad.latest].filter((x, i, arr) => x && arr.findIndex((y) => y && y.id === x.id) === i).map((s) => <SpotlightCard key={s.id} s={s} />)}
            <Link to="/bar-dad" className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 hover:border-rose-500/50 text-zinc-400 hover:text-white min-h-[180px] transition-colors">
              <span className="font-display font-bold">View All Spotlights →</span>
            </Link>
          </div>
        )}
      </Section>
      )}

      {settings.show_awards_home !== false && <AwardsHome count={settings.awards_home_count} />}

      {/* Across the Gulf Coast — all 5 states */}
      {sec.florida_alabama && (
      <Section title="Across the Gulf Coast" kicker="Five states, one scene">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {STATES.map((label) => {
            const items = data.states[label] || [];
            return (
              <div key={label} className="bg-[#111] border border-white/10 rounded-2xl p-6" data-testid={`state-${label}`}>
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-display font-bold text-xl">{label} <span className="text-zinc-500 text-sm font-normal">Events</span></h3>
                  <Link to={`/events?state=${label}`} className="text-sm text-rose-400">All →</Link>
                </div>
                <div className="space-y-3">
                  {items.slice(0, 3).map((e) => (
                    <Link key={e.id} to={`/events/${e.slug}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5">
                      <img src={e.event_image} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{e.title}</p>
                        <p className="text-sm text-zinc-400 flex items-center gap-1 truncate"><Calendar size={12} className="shrink-0" /> {fmtDateTime(e.event_date, e.start_time)} · {e.city}</p>
                      </div>
                    </Link>
                  ))}
                  {items.length === 0 && <p className="text-zinc-500 text-sm">No events yet.</p>}
                </div>
              </div>
            );
          })}
        </div>
      </Section>
      )}

      {/* CTA */}
      {sec.cta && (
      <section className="px-6 md:px-12 lg:px-20 py-14">
        <div className="relative rounded-3xl overflow-hidden p-10 md:p-16 text-center">
          <img src={COAST} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative">
            <h2 className="font-display font-black text-3xl md:text-5xl">Got an event, venue, or act?</h2>
            <p className="text-zinc-300 mt-4 max-w-xl mx-auto">List it on {settings.site_name} and reach thousands of music lovers across the coast.</p>
            <Link to="/submit" className="inline-block mt-8 bg-rose-500 hover:bg-rose-600 font-bold px-8 py-4 rounded-full transition-colors" data-testid="cta-submit">Submit for free</Link>
          </div>
        </div>
      </section>
      )}

      {/* Loyalty programs */}
      <RewardsRow />

      {/* Newsletter */}
      {sec.newsletter && (
      <section className="px-6 md:px-12 lg:px-20 pb-10">
        <div className="bg-[#111] border border-white/10 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display font-bold text-2xl">Never miss a show</h3>
            <p className="text-zinc-400 mt-1">Weekly drops of the best Gulf Coast events.</p>
          </div>
          <form onSubmit={subscribe} className="flex gap-3 w-full md:w-auto" data-testid="newsletter-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className="bg-[#0a0a0a] border border-white/10 rounded-full px-5 py-3 outline-none focus:border-rose-500 flex-1 min-w-0 md:w-72" data-testid="newsletter-email" />
            <button className="bg-rose-500 hover:bg-rose-600 font-bold px-6 py-3 rounded-full transition-colors shrink-0" data-testid="newsletter-submit">Subscribe</button>
          </form>
        </div>
      </section>
      )}
    </div>
  );
}
